import React, { useState, useEffect, useRef } from "react";
import { Headphones, X } from "lucide-react";
import "../AudioPlayer.css";

const AUDIO_SOURCES = {
  en: "https://yookicaimages.blob.core.windows.net/audio/yookinews_en.mp3",
  hi: "https://yookicaimages.blob.core.windows.net/audio/yookinews_hi.mp3",
};

export default function Audioplayer({ autoPlay = false, onClose }) {
  const [fridayDate, setFridayDate] = useState("");
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("yooki_news_lang") || "en";
    } catch {
      return "en";
    }
  });
  const audioRef = useRef(null);

  useEffect(() => {
    if (!autoPlay) return undefined;

    const timer = setTimeout(() => {
      audioRef.current?.play().catch(() => {
        // Autoplay may be blocked by the browser; ignore
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [autoPlay]);

  useEffect(() => {
    const updateDate = () => {
      const now = new Date();

      // Find last Friday
      const day = now.getDay(); // 0=Sun ... 5=Fri
      let lastFriday = new Date(now);
      lastFriday.setDate(now.getDate() - ((day + 2) % 7)); // go back to last Friday
      lastFriday.setHours(22, 10, 0, 0); // set to 10:10pm

      // If current time is before this week's Friday 10:10pm,
      // then show *previous* Friday
      if (now < lastFriday) {
        lastFriday.setDate(lastFriday.getDate() - 7);
      }

      // Format like "Sep 05, 2025"
      const options = { year: "numeric", month: "short", day: "2-digit" };
      setFridayDate(lastFriday.toLocaleDateString("en-US", options));
    };

    updateDate();

    // Check every minute to auto-update at exactly 10:10pm
    const interval = setInterval(updateDate, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleToggleLang = (selectedLang) => {
    if (selectedLang === lang) return;
    const isPlaying = audioRef.current && !audioRef.current.paused;
    setLang(selectedLang);
    try {
      localStorage.setItem("yooki_news_lang", selectedLang);
    } catch (_) {}

    if (audioRef.current) {
      audioRef.current.src = AUDIO_SOURCES[selectedLang];
      audioRef.current.load();
      if (isPlaying) {
        audioRef.current.play().catch(() => {});
      }
    }
  };

  return (
    <div
      className="audioplayer"
      onClick={() => {
        console.log("Audio player clicked");
      }}
    >
      <div className="audioplayer-header">
        <div className="audioplayer-title-wrap">
          <Headphones size={13} className="audioplayer-icon" />
          <span className="audioplayer-title">
            {lang === "hi" ? "साप्ताहिक समाचार" : "Weekly News"} - {fridayDate}
          </span>
        </div>
        <div className="audioplayer-actions">
          <div className="audio-lang-toggle" role="group" aria-label="Audio language toggle">
            <button
              type="button"
              className={`audio-lang-btn ${lang === "en" ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleLang("en");
              }}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
            <span className="audio-lang-divider">|</span>
            <button
              type="button"
              className={`audio-lang-btn ${lang === "hi" ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleLang("hi");
              }}
              aria-pressed={lang === "hi"}
            >
              HI
            </button>
          </div>
          {onClose && (
            <button
              type="button"
              className="ca-drawer-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              title="Close player"
              aria-label="Close audio player"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>
      <audio
        controls
        controlsList="nodownload"
        ref={audioRef}
        src={AUDIO_SOURCES[lang]}
        onClick={() => {
          console.log("Audio player clicked");
        }}
      >
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}
